import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState
} from 'react'
import { emptyArr } from '../../../constants/misc'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useUpdateQuestionnaireEntry } from '../../../hooks/questionnaire/useUpdateQuestionnaireEntry'
import { useProjectType } from '../../Project/Project.hooks'
import MarketVisibilityDialog from './MarketVisibilityDialog'
import { marketVisibilityErrorMessage } from './marketVisibilityError'

interface MarketVisibilityDialogContextValue {
  isMultiMarketV2: boolean
  /** Survey ids where this entry is hidden (stable ref while unchanged). */
  hiddenInMarkets: string[]
  openMarketVisibility: () => void
}

const MarketVisibilityDialogContext =
  createContext<MarketVisibilityDialogContextValue | null>(null)

/**
 * Access the current card's market-visibility dialog. Returns null outside a
 * provider (e.g. forks, previews), so callers must guard with `?.`.
 */
export const useMarketVisibilityDialog =
  (): MarketVisibilityDialogContextValue | null =>
    useContext(MarketVisibilityDialogContext)

interface Props {
  entry: DraftQuestionnaireEntry
  children: ReactNode
}

/**
 * Hosts the question-level MarketVisibilityDialog for a single card and exposes an
 * `openMarketVisibility` trigger via context, so the kebab menu, the header icon
 * (variant A) and the bottom banner (variant B) — which live in different subtrees
 * of the card — all open the same dialog without prop-drilling.
 */
export const MarketVisibilityDialogProvider: FC<Props> = ({
  entry,
  children
}) => {
  const { isMultiMarketV2 } = useProjectType()
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const { updateQuestionnaireEntry, loading } = useUpdateQuestionnaireEntry(
    entry.id
  )
  const { refetchQuestionnaire } = useGetDraftQuestionnaire()

  // emptyArr keeps a stable ref (so the memo below doesn't churn) when nothing is hidden.
  const hiddenInMarkets = entry.hiddenInMarkets ?? emptyArr

  const value = useMemo(
    () => ({
      isMultiMarketV2,
      hiddenInMarkets,
      openMarketVisibility: () => setOpen(true)
    }),
    [isMultiMarketV2, hiddenInMarkets]
  )

  // Same label the kebab used before the dialog was lifted here.
  const targetLabel =
    (entry.entryItem as { question?: { text?: string } }).question?.text ||
    `question ${entry.number}`

  return (
    <MarketVisibilityDialogContext.Provider value={value}>
      {children}
      {isMultiMarketV2 && (
        <MarketVisibilityDialog
          open={open}
          targetLabel={targetLabel}
          initialHiddenInMarkets={hiddenInMarkets}
          saving={loading}
          onClose={() => setOpen(false)}
          onSave={async (nextHidden) => {
            try {
              await updateQuestionnaireEntry({ hiddenInMarkets: nextHidden })
              setOpen(false)
              refetchQuestionnaire()
            } catch (error) {
              // Keep the dialog open so the user can deselect the offending market.
              enqueueSnackbar(marketVisibilityErrorMessage(error), {
                variant: SnackbarVariant.Error
              })
            }
          }}
        />
      )}
    </MarketVisibilityDialogContext.Provider>
  )
}
